import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { tuiPure, TuiStringHandler, TuiContext } from '@taiga-ui/cdk';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { AlertService } from '@src/core/services';
import { ViewMode } from '@src/models';
import { BaseResizableComponent } from '@src/app/modules/base';
import { convertDateToApiFormat } from '@src/api';
import { convertDateToTuiDayAndTuiTime, convertTuiDayAndTuiTimeToDate } from '@src/utils';
import { TIME_ITEMS } from '@src/core/constants';

import { ProductRequestType, STATUS_TYPE, StatusType } from '../../types';

import { FormDataControlsType, FormDataType } from './types';

@Component({
  selector: 'lp-product-request-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpProductRequestEditComponent extends BaseResizableComponent implements OnChanges {
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);

  @Input() data?: ProductRequestType | null;
  @Input() mode?: ViewMode;
  @Input() loading = true;

  @Output() saveProductRequest = new EventEmitter<ProductRequestType>();
  @Output() cancelEdit = new EventEmitter<void>();

  form!: FormGroup<FormDataControlsType>;

  readonly timeItems = TIME_ITEMS;
  readonly statusTypes: StatusType[] = STATUS_TYPE;

  constructor() {
    super();

    this.initForm();
  }

  // TODO: refactoring
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.loading = true;

      if (this.data) {
        const permissions = this.ngxPermissionsService.getPermissions();
        const mappedData = this.mapProductRequestTypeToFormData(this.data);
        this.form.patchValue(mappedData);

        this.form.disable();
        this.form.controls.comment.enable();

        // TODO: почему это не сделано в mapProductRequestTypeToFormData ?
        if ('loyaltyProgramProductRequestEditing' in permissions) {
          const commentDayControl = this.form.controls.commentDay;
          const commentTimeControl = this.form.controls.commentTime;

          const [tuiDay, tuiTime] = convertDateToTuiDayAndTuiTime(new Date());

          this.form.controls.status.enable();
          commentDayControl.enable();
          commentTimeControl.enable();

          commentDayControl.patchValue(tuiDay);
          commentTimeControl.patchValue(tuiTime);
        }
      } else {
        this.form.reset();
        this.initForm();
      }

      this.loading = false;
    }

    this.cdr.markForCheck();
  }

  onClickSaveButton() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillAndCheckFields'));
      return;
    }

    const productRequestData = this.mapFormDataToProductRequestType(this.form.getRawValue());

    this.saveProductRequest.emit(productRequestData);
  }

  @tuiPure
  statusTypesStringify(items: ReadonlyArray<StatusType>): TuiStringHandler<TuiContext<number>> {
    const map = new Map(items.map(({ id, value }) => [id, value] as [number, string]));

    return ({ $implicit }: TuiContext<number>) => this.translateService.instant(map.get($implicit) || ' ');
  }

  private initForm(): void {
    this.form = new FormGroup<FormDataControlsType>({
      id: new FormControl(null),
      name: new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }),
      location: new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }),
      mapLink: new FormControl(null, { validators: [Validators.maxLength(255)] }),
      webLink: new FormControl(null, { validators: [Validators.maxLength(255)] }),
      reasonText: new FormControl(null, { validators: [Validators.maxLength(255)] }),
      readyToPromote: new FormControl(null),
      userContactsText: new FormControl(null, { validators: [Validators.maxLength(255)] }),
      status: new FormControl(1),
      comment: new FormControl(null, { validators: [Validators.maxLength(500)] }),
      commentDay: new FormControl(null),
      commentTime: new FormControl(null),
      managerContact: new FormControl(null, { validators: [Validators.maxLength(500)] }),
    });
  }

  private mapProductRequestTypeToFormData(productRequest: ProductRequestType) {
    const {
      id,
      name,
      location,
      mapLink,
      webLink,
      reasonText,
      readyToPromote,
      userContactsText,
      status,
      managerContact,
    } = productRequest;

    return <FormDataType>{
      id,
      name,
      location,
      mapLink,
      webLink,
      reasonText,
      readyToPromote,
      userContactsText,
      status,
      managerContact,
    };
  }

  private mapFormDataToProductRequestType(formData: FormDataType) {
    const {
      id,
      name,
      location,
      mapLink,
      webLink,
      reasonText,
      readyToPromote,
      userContactsText,
      comment,
      commentDay,
      commentTime,
      status,
      managerContact,
    } = formData;

    const commentDate = convertDateToApiFormat(convertTuiDayAndTuiTimeToDate(commentDay, commentTime));

    return <ProductRequestType>{
      id,
      name,
      location,
      mapLink,
      webLink,
      reasonText,
      readyToPromote: Boolean(readyToPromote),
      userContactsText,
      managerContact,
      newComment: {
        status,
        requestId: id,
        changeDate: commentDate,
        changeText: comment,
      },
    };
  }
}
